// 常量：浏览器内仅保留当前待完成的充值意图，供支付外跳和刷新后恢复流程
const RECHARGE_INTENT_STORAGE_KEY = 'stellarcore_pending_recharge_intent'

// 方法：把接口返回的分值安全转换为元
export function centsToAmount(value) {
  const cents = Number(value)
  return Number.isFinite(cents) ? cents / 100 : 0
}

// 方法：把钱包接口响应映射为页面统一使用的余额模型
export function mapWalletBalance(wallet) {
  const source = wallet || {}
  return {
    total: centsToAmount(source.total_cents),
    base: centsToAmount(source.base_balance_cents),
    bonus: centsToAmount(source.bonus_balance_cents),
    expiringBonus: centsToAmount(source.expiring_bonus_cents),
    expiringAt: source.expiring_bonus_at || null
  }
}

// 方法：校验并排序充值档位，零金额档位保留为基础赠送规则
export function normalizeRechargeTiers(payload) {
  const tiers = payload && Array.isArray(payload.tiers) ? payload.tiers : []
  const normalized = tiers
    .map(item => ({
      amountCents: Number(item && item.amount_cents),
      bonusRate: Number(item && item.bonus_rate)
    }))
    .filter(item => (
      Number.isInteger(item.amountCents) &&
      item.amountCents >= 0 &&
      Number.isFinite(item.bonusRate) &&
      item.bonusRate >= 0
    ))
    .sort((left, right) => left.amountCents - right.amountCents)

  // 逻辑分支：没有可用规则时抛出错误，禁止页面使用未知赠送比例下单
  if (normalized.length === 0) {
    throw new Error('充值档位数据为空')
  }

  return normalized.map(item => ({
    ...item,
    amount: centsToAmount(item.amountCents)
  }))
}

// 方法：按不超过充值金额的最高档位计算服务端预期赠送额度
export function calculateRechargeQuote(amount, tiers) {
  const amountCents = Math.round((Number(amount) + Number.EPSILON) * 100)
  if (!Number.isInteger(amountCents) || amountCents <= 0 || !Array.isArray(tiers) || tiers.length === 0) {
    return null
  }

  const matchedTier = tiers.reduce((matched, tier) => (
    tier.amountCents <= amountCents ? tier : matched
  ), tiers[0])
  const rate = matchedTier && matchedTier.amountCents <= amountCents
    ? matchedTier.bonusRate
    : 0
  const bonusCents = Math.round(amountCents * rate / 100)

  return {
    amount: centsToAmount(amountCents),
    amountCents,
    rate,
    base: centsToAmount(amountCents),
    bonus: centsToAmount(bonusCents),
    bonusCents,
    total: centsToAmount(amountCents + bonusCents),
    totalCents: amountCents + bonusCents
  }
}

// 方法：读取当前待完成充值意图，订单不匹配时不复用其他流程数据
export function readRechargeIntent(orderId = '') {
  try {
    const value = window.localStorage.getItem(RECHARGE_INTENT_STORAGE_KEY)
    const intent = value ? JSON.parse(value) : null
    if (!intent || (orderId && intent.rechargeOrderId !== orderId)) {
      return null
    }
    return intent
  } catch (error) {
    return null
  }
}

// 方法：按支付宝回传的商户订单号查找本地待完成充值订单 ID
export function findRechargeOrderIdByOrderNumber(orderNumber = '') {
  const intent = readRechargeIntent()
  if (!intent || !orderNumber || intent.rechargeOrderNumber !== orderNumber) {
    return ''
  }
  return intent.rechargeOrderId || ''
}

// 方法：保存当前充值订单及后续套餐购买上下文
export function saveRechargeIntent(intent) {
  try {
    window.localStorage.setItem(
      RECHARGE_INTENT_STORAGE_KEY,
      JSON.stringify({ ...intent, updatedAt: Date.now() })
    )
  } catch (error) {
    // 浏览器禁用存储时仍允许当前页面继续支付，刷新后不再恢复自动购买上下文。
  }
  return intent
}

// 方法：合并更新当前充值意图，用于持久化后续套餐订单 ID
export function updateRechargeIntent(orderId, patch) {
  const current = readRechargeIntent(orderId)
  if (!current) {
    return null
  }
  const next = { ...current, ...patch }
  saveRechargeIntent(next)
  return next
}

// 方法：仅清理指定订单对应的充值意图，避免误删新开启的充值流程
export function clearRechargeIntent(orderId = '') {
  try {
    const current = readRechargeIntent()
    if (!current || (orderId && current.rechargeOrderId !== orderId)) {
      return
    }
    window.localStorage.removeItem(RECHARGE_INTENT_STORAGE_KEY)
  } catch (error) {
    // 浏览器禁用存储时无需额外处理。
  }
}
